"use client";

import React, { Fragment, useEffect, useState } from "react";

import { motion } from "framer-motion";
import Image, { GroqImage } from "@/helpers/Image";

import { PortableText } from "@portabletext/react";
import {
  customRichTextComponent,
  richTextComponent,
} from "@/sanity/sanityPortableText";

import { CTAButton } from "@/shadcn/ui/button";
import logo from "@/public/logo-black.svg";
import type { PageAffiliateSchema } from "@/sanity/pages/PageAffiliateTemplate";
import lastBG from "@/public/affiliate-overview/last-bg.png";
import lastStatue from "@/public/affiliate-overview/last-statue.png";
import dotsBG from "@/public/affiliate-overview/dots-bg.png";
import stepCardBG from "@/public/affiliate-overview/step-card-bg.png";

import GradualBlur from "@/shadcn/GradualBlur";
import { cn } from "@/lib/utils";
import Countdown from "@/components/global/countdown/Countdown";
import Link from "next/link";
import arrow from "@/public/global/dotted-arrow.svg";
import { PageAssetsSchema } from "@/sanity/pages/PageAssetsTemplate";
import bgDots from "@/public/assets-page/whatCardbg.png";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselApi,
} from "@/shadcn/ui/carousel";

export default function BestPerformers({ data }: { data: PageAssetsSchema }) {
  const ease = [0.25, 0.1, 0.25, 1] as const;

  const certificates = data?.bestPerformersCertificatesRefs?.length
    ? data.bestPerformersCertificatesRefs
        .map((c) => c.image)
        .filter((img): img is NonNullable<typeof img> => Boolean(img))
    : data?.bestPerformersCertificates;

  const [testimonialsApi, setTestimonialsApi] = useState<CarouselApi>();
  const [testimonialsCurrent, setTestimonialsCurrent] = useState(0);
  const [testimonialsCount, setTestimonialsCount] = useState(0);
  return (
    <section className="relative w-full lg:p-[4.75rem]">
      <div className="flex relative flex-col bg-[#04271D] py-[10rem] gap-[4rem] overflow-clip lg:py-[15rem] lg:gap-[7rem]">
        <div className="w-[30%] bg-gradient-to-r from-[#04271D] to-transparent absolute z-[9] h-full left-0 lg:block hidden pointer-events-none" />
        <div className="w-[30%] bg-gradient-to-l from-[#04271D] to-transparent absolute z-[9] h-full right-0 lg:block hidden pointer-events-none" />

        <Image
          src={dotsBG}
          alt=""
          className="object-cover absolute inset-0 w-full h-full pointer-events-none mix-blend-exclusion"
        />

        <div className="hidden absolute w-full h-[70%] lg:block pointer-events-none">
          <GradualBlur
            position="right"
            strength={1}
            width="20%"
            divCount={8}
            exponential
            zIndex={99}
            curve="ease-out"
            style={{ overflow: "hidden" }}
          />
          <GradualBlur
            position="left"
            strength={1}
            width="20%"
            divCount={8}
            exponential
            zIndex={99}
            curve="ease-out"
            style={{ overflow: "hidden" }}
          />
        </div>
        <div className="lg:px-0 px-[3rem]">
          {data?.bestPerformersText && (
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.35 }}
              transition={{ duration: 0.8, ease }}
              className="richtext richtext-center mx-auto lg:max-w-[77.625rem] lg:[&_span]:font-display lg:[&_span]:font-[700] [&_span]:font-instrument-sans [&_span]:font-[600] [&_span]:text-white lg:[&_span]:text-[#A3DEB2] lg:[&_br]:block [&_br]:hidden [&_span]:pl-[1rem] lg:[&_span]:pl-0 prose-h6:font-blacker prose-h6:italic prose-h6:text-[2.25rem] prose-h6:text-white prose-h6:mb-[2rem] prose-p:w-[min(58.125rem,100%)] prose-p:text-white/80 prose-strong:!font-[600] prose-headings:font-geist prose-headings:font-[500] prose-headings:text-white prose-headings:mb-[1rem]"
            >
              <PortableText
                value={data.bestPerformersText}
                components={richTextComponent}
              />
            </motion.div>
          )}
        </div>
        {certificates && certificates.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, ease, delay: 0.15 }}
            className="w-full"
          >
            <Carousel
              opts={{ align: "start", loop: true, dragFree: true }}
              className="w-full"
            >
              <CarouselContent className="-ml-[3rem]">
                {certificates.map((item, idx) => (
                  <CarouselItem
                    key={idx}
                    className="pl-[3rem] basis-[85%] lg:basis-[26%]"
                  >
                    <div className="aspect-[369/251] overflow-hidden rounded-[0.25rem]">
                      <GroqImage
                        src={item}
                        alt={item.name || ""}
                        className="!w-full !h-full object-cover"
                      />
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          </motion.div>
        )}

        {data?.bestPerformersCTA && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.35 }}
            transition={{ duration: 0.8, ease, delay: 0.2 }}
            className="flex justify-center lg:[&_.button]:!min-h-[5.75rem] px-[3rem] [&>div]:w-full lg:[&>div]:w-fit"
          >
            <CTAButton data={data.bestPerformersCTA} />
          </motion.div>
        )}
      </div>
    </section>
  );
}
